import React from "react";
import { apiRiwayatPos, formatApiErrorDetail } from "@/lib/api";
import * as W from "@/lib/waktu";
import { Loader2, RefreshCw } from "lucide-react";

const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-200";
const STATUS_CLS = { "Siap diambil": "text-emerald-300", "Sudah diambil": "text-slate-400", Batal: "text-red-300" };

// Tahap 2 / T2-B (owner 2026-10-08): finished work of my station — default my
// own, last 31 days (WIT); "semua" = every operator of the station; the
// Owner picks any station. No money.
export default function RiwayatPos({ isAdmin, posList }) {
  const [f, setF] = React.useState({ dari: "", sampai: "", semua: false, pos_code: "" });
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = {};
      if (f.dari) p.dari = f.dari;
      if (f.sampai) p.sampai = f.sampai;
      if (f.semua) p.semua = true;
      if (isAdmin && f.pos_code) p.pos_code = f.pos_code;
      setD(await apiRiwayatPos(p));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setLoading(false); }
  }, [f, isAdmin]);
  React.useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="riwayat-pos" className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
      <div className="flex flex-wrap items-end gap-3 px-5 py-3 border-b border-slate-800 text-xs text-slate-400">
        <label>Dari <input data-testid="rp-dari" type="date" value={f.dari} onChange={(e) => setF({ ...f, dari: e.target.value })} className={`${inputCls} block`} /></label>
        <label>Sampai <input data-testid="rp-sampai" type="date" value={f.sampai} onChange={(e) => setF({ ...f, sampai: e.target.value })} className={`${inputCls} block`} /></label>
        {isAdmin ? (
          <label>Pos <select data-testid="rp-pos" value={f.pos_code} onChange={(e) => setF({ ...f, pos_code: e.target.value })} className={`${inputCls} block`}>
            <option value="">Semua pos</option>{posList.map((p) => <option key={p.code} value={p.code}>{p.nama || p.code}</option>)}</select></label>
        ) : (
          <label className="inline-flex items-center gap-1.5 pb-2"><input data-testid="rp-semua" type="checkbox" checked={f.semua} onChange={(e) => setF({ ...f, semua: e.target.checked })} /> Semua operator pos ini (default: pekerjaan saya)</label>
        )}
        <span className="pb-2">{d ? `${W.tanggal(d.dari + "T12:00:00Z")} – ${W.tanggal(d.sampai + "T12:00:00Z")} · maks. 31 hari` : ""}</span>
        <button onClick={load} className="ml-auto pb-2 inline-flex items-center gap-1 text-slate-400 hover:text-slate-200"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Muat ulang</button>
      </div>
      {error && <p data-testid="rp-error" className="px-5 py-2 text-sm text-red-300">{error}</p>}
      {!d ? <div className="p-8 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" size={16} />Memuat…</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rp-table">
            <thead><tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
              <th className="px-4 py-2">Selesai</th><th className="px-4 py-2">SPK</th><th className="px-4 py-2">Customer</th><th className="px-4 py-2">Pos</th>
              <th className="px-4 py-2">Operator</th><th className="px-4 py-2 text-right">Hasil</th><th className="px-4 py-2">Catatan</th><th className="px-4 py-2">Status sekarang</th></tr></thead>
            <tbody>
              {d.baris.map((b, i) => (
                <tr key={`${b.spk_id}-${b.pos_code}-${b.selesai}-${i}`} data-testid={`rp-row-${b.nomor_spk}-${b.pos_code}`} className="border-b border-slate-800/60">
                  <td className="px-4 py-2 text-xs text-slate-400 whitespace-nowrap">{W.waktu(b.selesai)}</td>
                  <td className="px-4 py-2 font-mono text-amber-300">{b.nomor_spk}<span className="block text-[11px] text-slate-500">{b.nomor_faktur}</span></td>
                  <td className="px-4 py-2 text-slate-300">{b.customer || "—"}</td>
                  <td className="px-4 py-2 text-xs">{b.pos_nama}{b.pos_alur && <div data-testid={`rp-alur-${b.nomor_spk}`} className="text-[10px] text-slate-500">pos alternatif untuk {b.pos_alur_nama}</div>}</td>
                  <td className="px-4 py-2 text-xs">{b.operator || "—"}{b.milik_saya ? <span className="ml-1 text-cyan-300">(saya)</span> : null}</td>
                  <td className="px-4 py-2 text-right font-mono">{b.hasil != null ? `${b.hasil}${b.reject ? ` (+${b.reject} reject)` : ""} ${b.satuan === "POTONG" ? "potong" : "pcs"}` : "—"}</td>
                  <td className="px-4 py-2 text-xs">
                    {b.rework_ke ? <span className="text-amber-300">rework ke-{b.rework_ke}</span> : null}
                    {b.penyebab_reject ? <span className="block text-red-300">⚠ penyebab reject ({b.penyebab_reject}×)</span> : null}
                  </td>
                  <td className={`px-4 py-2 text-xs ${STATUS_CLS[b.status] || "text-slate-300"}`}>{b.status}{b.posisi ? ` · di ${b.posisi}` : ""}</td>
                </tr>
              ))}
              {d.baris.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Belum ada pekerjaan selesai pada rentang ini.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
